import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Check } from "lucide-react";

const fmt = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null);
const ETA_CLASS = { approved: "text-charcoal", pending: "text-warmgrey", passed: "text-amber-800", received: "text-sage" };

export const DealerShipmentCard = ({ shipment: s, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const toggleFollow = async () => {
    setBusy(true);
    try {
      if (s.subscribed) await api.delete(`/dealer/shipments/${s.container_id}/subscribe`);
      else await api.post(`/dealer/shipments/${s.container_id}/subscribe`);
      toast.success(s.subscribed ? "You will no longer receive updates for this shipment" : "You will be notified about milestones and approved date changes");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <article className="rounded-md border border-border bg-white p-5 shadow-[0_2px_12px_rgba(26,26,24,0.04)]" style={{ borderLeft: `4px solid ${s.status === "completed" ? "#96A58F" : "#B86F54"}` }} data-testid={`shipment-card-${s.reference}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-warmgrey">{s.reference}</p>
          <h2 className="mt-1 text-base font-medium text-charcoal md:text-lg" data-testid={`shipment-stage-${s.reference}`}>{s.stage_label}</h2>
          {s.latest_update && <p className="mt-1 text-xs text-warmgrey" data-testid={`shipment-latest-${s.reference}`}>Latest update: {s.latest_update.text}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {s.delayed && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-900" data-testid={`shipment-delayed-${s.reference}`}>Date moved later</span>}
          {s.relevant && <span className="rounded-full bg-sand px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-charcoal" data-testid={`shipment-relevant-${s.reference}`}>Your reservation / order</span>}
          {s.status === "incoming" && (
            <Button size="sm" variant="outline" onClick={toggleFollow} disabled={busy} data-testid={`shipment-follow-${s.reference}`} className="h-8 border-limestone text-xs">
              {s.subscribed ? <><BellOff className="mr-1 h-3 w-3" /> Unfollow</> : <><Bell className="mr-1 h-3 w-3" /> Follow updates</>}
            </Button>
          )}
        </div>
      </div>

      <ol className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid={`shipment-milestones-${s.reference}`}>
        {s.milestones.map((m, i) => (
          <li key={m.code} data-testid={`milestone-${s.reference}-${m.code}`} data-state={m.state} className={`rounded-md border p-2.5 ${m.state === "current" ? "border-terracotta bg-terracotta/5" : m.state === "done" ? "border-sage/60 bg-sage/10" : "border-border"}`}>
            <div className="flex items-center gap-1.5">
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold ${m.state !== "pending" ? "bg-charcoal text-white" : "border border-border text-warmgrey"}`}>{m.state !== "pending" ? <Check className="h-2.5 w-2.5" /> : i + 1}</span>
              <p className={`text-[11px] font-medium leading-tight ${m.state !== "pending" ? "text-charcoal" : "text-warmgrey"}`}>{m.label}</p>
            </div>
            <p className="mt-1.5 text-[11px] text-warmgrey">{m.date ? fmt(m.date) : m.date_state === "to_be_confirmed" ? "date to be confirmed" : "pending"}</p>
          </li>
        ))}
      </ol>

      <div className="mt-4 rounded-md bg-sand/40 px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-warmgrey">Warehouse availability</p>
        <p className={`mt-1 text-sm font-medium ${ETA_CLASS[s.warehouse_eta_state]}`} data-testid={`shipment-eta-${s.reference}`}>{s.warehouse_eta && s.warehouse_eta_state === "approved" ? fmt(s.warehouse_eta) : s.warehouse_eta_message}</p>
        {s.eta_history.length > 0 && (
          <p className="mt-1 text-xs text-warmgrey" data-testid={`shipment-eta-history-${s.reference}`}>
            {s.eta_history.length} date update{s.eta_history.length === 1 ? "" : "s"} · last: {s.eta_history[s.eta_history.length - 1].from ? `${fmt(s.eta_history[s.eta_history.length - 1].from)} → ` : ""}{fmt(s.eta_history[s.eta_history.length - 1].to)}
          </p>
        )}
      </div>

      {/* ID-081: concise summary — product-level availability and reservations live in Inventory */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3 text-sm" data-testid={`shipment-summary-${s.reference}`}>
        <p className="text-charcoal">
          {s.contents.length} product{s.contents.length === 1 ? "" : "s"} · {s.total_boxes} boxes {s.status === "completed" ? "received" : "expected"}
          {s.status !== "completed" && s.pre_reservation_enabled && <span className="text-warmgrey"> · {s.available_to_reserve_boxes} boxes available to pre-reserve</span>}
        </p>
        <Link to={`/dealer/inventory?tab=${s.status === "completed" ? "florida" : "transit"}&shipment=${encodeURIComponent(s.reference)}&skus=${encodeURIComponent(s.contents.map((p) => p.sku).join(","))}`}
          className="text-xs font-semibold text-terracotta underline-offset-2 hover:underline" data-testid={`shipment-view-inventory-${s.reference}`}>
          {s.status === "completed" ? "View Florida inventory" : "View incoming inventory"} →
        </Link>
      </div>
    </article>
  );
};
